/**
 * sl-4wjh.2: Generic capability-based feature tab.
 *
 * DRY base for ImageGen, Transcription, and Websearch tabs. Fetches
 * per-user or per-automation data depending on the active context,
 * converts to BreakdownRow[], and renders via FeatureTabShell.
 */
import { useI18n } from '@affine/i18n';
import { useCallback, useMemo } from 'react';

import { fetchCapabilityUsers, fetchCapabilityWorkflows } from '../api';
import { FeatureTabShell } from '../feature-tab-shell';
import { formatCount, formatCredits, formatTopModelLabel } from '../format';
import {
  computeTopModel,
  computeTotalCalls,
  type FetchResult,
  toBreakdownRows,
} from '../row-utils';
import type { SummaryCardData } from '../tab-summary-card';
import type { AnalyticsPeriodRange, ContextType } from '../types';
import { useAsyncResource } from '../use-async-resource';

const I18N = 'com.streamlain.settings.workspace.ai.analytics' as const;

export interface CapabilityFeatureTabProps {
  workspaceId: string;
  period: string | AnalyticsPeriodRange;
  context: ContextType;
  /** Backend capability name to filter by (e.g. 'image', 'audio', 'websearch'). */
  capability: string;
  /** Cache key prefix for dedup (e.g. 'image-gen'). */
  cacheKey: string;
  /** i18n key suffix for the count card label (e.g. 'images-generated'). */
  countCardKey: string;
  /** Fallback label for the count card. */
  countCardDefault: string;
}

export const CapabilityFeatureTab = ({
  workspaceId,
  period,
  context,
  capability,
  cacheKey,
  countCardKey,
  countCardDefault,
}: CapabilityFeatureTabProps) => {
  const t = useI18n();
  const isWorkflow = context === 'workflow';
  const periodCacheKey =
    typeof period === 'string'
      ? period
      : `${period.key}:${period.since}:${period.until}`;

  const fetcher = useCallback(
    (): Promise<FetchResult> =>
      isWorkflow
        ? fetchCapabilityWorkflows(workspaceId, period, capability)
        : fetchCapabilityUsers(workspaceId, period, capability),
    [workspaceId, period, capability, isWorkflow]
  );

  const { data, isLoading, error } = useAsyncResource(
    `${cacheKey}:${context}:${workspaceId}:${periodCacheKey}`,
    fetcher
  );

  const rows = useMemo(
    () => (data ? toBreakdownRows(data, isWorkflow) : []),
    [data, isWorkflow]
  );

  const topModel = useMemo(() => computeTopModel(rows), [rows]);
  const totalCalls = useMemo(() => computeTotalCalls(rows), [rows]);
  // Compute from context-scoped rows so user- and workflow-tabs don't share
  // the context-agnostic featureTotals bucket total (would otherwise mix
  // workflow-step usage into the user-tab total, and vice versa).
  const totalCredits = useMemo(
    () => rows.reduce((sum, row) => sum + row.credits, 0),
    [rows]
  );

  const summary: readonly [SummaryCardData, SummaryCardData, SummaryCardData] =
    [
      {
        label: t[`${I18N}.card.total-credits`]?.() ?? 'Total credits',
        value: formatCredits(totalCredits),
      },
      {
        label: t[`${I18N}.card.${countCardKey}`]?.() ?? countCardDefault,
        value: formatCount(totalCalls),
      },
      {
        label: t[`${I18N}.card.top-model`]?.() ?? 'Top model',
        value: topModel ? formatTopModelLabel(topModel.model) : '-',
        subtext: topModel
          ? `${formatCredits(topModel.credits)} credits`
          : undefined,
        smallValue: true,
      },
    ] as const;

  return (
    <FeatureTabShell
      summary={summary}
      rows={rows}
      nameHeader={
        isWorkflow
          ? (t[`${I18N}.table.workflow-header`]?.() ?? 'Workflow')
          : (t[`${I18N}.table.user-header`]?.() ?? 'User')
      }
      isLoading={isLoading}
      error={error}
      resetSignal={`${periodCacheKey}:${context}`}
      hideUserFilter={isWorkflow}
    />
  );
};
